import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import BlogLayout from '../components/BlogLayout';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (slug) {
      fetchBlogPost();
      fetchRelatedPosts();
    }
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`);
      
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else if (response.status === 404) {
        setError('Blog post not found');
      } else {
        setError('Failed to load blog post');
      }
    } catch (err) {
      setError('Error loading blog post');
      console.error('Blog post fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog/related/${slug}?limit=3`);
      
      if (response.ok) {
        const data = await response.json();
        setRelatedPosts(data);
      }
    } catch (err) {
      console.error('Related posts fetch error:', err);
    }
  };

  // post.content is set via an admin-only endpoint, but rendering it
  // unsanitized would still mean a single compromised admin account (or a
  // future edit path we haven't thought of) becomes stored XSS for every
  // visitor who reads the post - sanitize to the tags our own markdown
  // converter actually produces (see populate_all_blog_posts.py).
  const createMarkup = (html) => {
    return {
      __html: DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'ul', 'li', 'strong', 'em', 'a', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
      }),
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Loading Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>

        {/* Loading Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
          <p className="text-gray-600 mb-6">
            {error === 'Blog post not found' 
              ? "Sorry, we couldn't find the article you're looking for. It may have been moved or deleted."
              : "We're having trouble loading this article right now."
            }
          </p>
          
          <div className="space-y-3">
            <Link
              to="/blog"
              className="block w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse All Articles
            </Link>
            
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-full text-gray-600 hover:text-gray-800 px-6 py-3"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </button>
          </div>

          {/* Helpful Links */}
          <div className="mt-8 pt-6 border-t text-sm text-gray-500">
            <p className="mb-2">Looking for help? Try these resources:</p>
            <div className="space-y-1">
              <Link to="/search" className="block text-blue-600 hover:text-blue-800">
                Find Treatment Centers
              </Link>
              <Link to="/states" className="block text-blue-600 hover:text-blue-800">
                Browse by State
              </Link>
              <a href="tel:1-888-968-1014" className="block text-blue-600 hover:text-blue-800">
                Call 1-888-968-1014
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <BlogLayout
      title={post.title}
      metaDescription={post.meta_description}
      author={post.author}
      publishedDate={post.published_date}
      updatedDate={post.updated_date}
      readTime={post.read_time}
      category={post.category}
      tags={post.tags}
      featuredImage={post.featured_image}
      featuredImageAlt={post.featured_image_alt}
      relatedPosts={relatedPosts}
    >
      {/* Article Content */}
      <div 
        className="blog-content"
        dangerouslySetInnerHTML={createMarkup(post.content)}
      />

      {/* Social Sharing (optional) */}
      <div className="mt-8 pt-6 border-t">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Share this article</h4>
        <div className="flex space-x-4">
          <button
            onClick={() => {
              const url = window.location.href;
              const text = `${post.title} - ${post.meta_description}`;
              if (navigator.share) {
                navigator.share({ title: post.title, text, url });
              } else {
                // Fallback: copy to clipboard
                navigator.clipboard.writeText(url);
                alert('Link copied to clipboard!');
              }
            }}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Share
          </button>
          
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            Twitter
          </a>
          
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
          >
            Facebook
          </a>
        </div>
      </div>

      {/* Navigation to Blog */}
      <div className="mt-8 text-center">
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to All Articles
        </Link>
      </div>
    </BlogLayout>
  );
};

export default BlogPostPage;